import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, UrlTree } from "@angular/router";
import { FeatureAvailabilityService } from "@core-services/feature-availability.service";
import { Feature } from "@models/feature-list";
import { Observable, of } from "rxjs";
import { catchError, map, skipWhile, switchMap, timeout } from "rxjs/operators";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { STAKE_TYPE } from "@helpers/constants";
import { StakedAssetFacade } from "@modules/staking/domain/+state/staked-asset.facade";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";

@Injectable({
	providedIn: "root"
})
export class StakingGuard {
	constructor(
		private readonly featureAvailabilityService: FeatureAvailabilityService,
		private readonly stakedAssetFacade: StakedAssetFacade,
		private readonly router: Router
	) {}

	public canActivate(
		route: ActivatedRouteSnapshot
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		const flowType: TRADE_TYPE | STAKE_TYPE = route.data["flowType"];
		const featureFlag: Feature =
			flowType === TRADE_TYPE.UNSTAKE || flowType === STAKE_TYPE.OVERVIEW
				? Feature.STAKING
				: Feature.STAKING_GLOBAL;

		return this.featureAvailabilityService.getAvailability(featureFlag).pipe(
			skipWhile((enabled: boolean) => enabled === null),
			timeout(2500),
			switchMap((enabled: boolean) => {
				if (!enabled) {
					return of(this.getFallbackTree());
				}

				if (flowType === TRADE_TYPE.UNSTAKE) {
					return this.validateStakeableForUnstake(route);
				}

				if (flowType === TRADE_TYPE.STAKE) {
					return this.validateStakeableForStake(route);
				}

				return of(true);
			})
		);
	}

	private validateStakeableForUnstake(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
		const stakeableId = this.getStakeableId(route);
		if (!stakeableId) {
			return of(this.getFallbackTree());
		}

		return this.stakedAssetFacade.fetchAndUpdateStakable(stakeableId).pipe(
			map((stakeable) => {
				if (stakeable && +stakeable.stakedAmount > 0) {
					return true;
				}
				return this.getFallbackTree();
			}),
			catchError(() => of(this.getFallbackTree()))
		);
	}

	private validateStakeableForStake(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
		const stakeableId = this.getStakeableId(route);
		if (!stakeableId) {
			return of(true);
		}

		return this.stakedAssetFacade.fetchAndUpdateStakable(stakeableId).pipe(
			map((stakeable) => {
				if (stakeable && stakeable.availability === StakeableAvailability.ENABLED) {
					return true;
				}
				return this.getFallbackTree();
			}),
			catchError(() => of(this.getFallbackTree()))
		);
	}

	private getStakeableId(route: ActivatedRouteSnapshot): string {
		return (
			route.queryParamMap.get("assetId") ??
			route.queryParamMap.get("stakeableId") ??
			route.paramMap.get("assetId") ??
			route.parent?.paramMap?.get("assetId") ??
			""
		);
	}

	private getFallbackTree(): UrlTree {
		return this.router.parseUrl("/home");
	}
}
